import { t } from '../i18n';
import { SITE } from '../site';
import { post, SITE_HEADER } from './client';
import type { Media } from './types';

export const ACCEPTED_IMAGES = 'image/webp,image/png,image/jpeg,image/avif';
export const ACCEPTED_PDF = 'application/pdf,.pdf';

interface UploadTicket {
  media: Media;
  upload: { method: 'PUT'; url: string; headers: Record<string, string> };
}

/** PUT con XMLHttpRequest para poder mostrar el progreso. */
function putWithProgress(url: string, file: File, headers: Record<string, string>, onProgress?: (p: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    // En desarrollo el archivo va a la propia API (que pide la sesión de esta marca); en AWS va
    // directo a S3 con una URL firmada, que no admite encabezados de más.
    if (url.startsWith('/')) xhr.setRequestHeader(SITE_HEADER, SITE);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(t('La subida falló ({status})', { status: xhr.status }))));
    xhr.onerror = () => reject(new Error(t('Se cortó la conexión durante la subida')));
    xhr.send(file);
  });
}

/**
 * Sube un archivo: la API reserva el lugar (S3 firmado en AWS, la propia API en local),
 * el navegador manda el archivo y la API comprueba que sea lo que dice ser.
 */
async function uploadFile(file: File, contentType: string, onProgress?: (p: number) => void): Promise<Media> {
  const ticket = await post<UploadTicket>('/admin/media/uploads', { filename: file.name, contentType, bytes: file.size });
  await putWithProgress(ticket.upload.url, file, ticket.upload.headers, onProgress);
  const { media } = await post<{ media: Media }>(`/admin/media/${ticket.media.id}/complete`);
  return media;
}

/** Sube una foto; la API además lee sus medidas. Solo imágenes: un PDF no va donde va una foto. */
export async function uploadImage(file: File, onProgress?: (p: number) => void): Promise<Media> {
  const type = file.type || guessType(file.name);
  if (!ACCEPTED_IMAGES.split(',').includes(type)) throw new Error(t('Formato no admitido: usa WebP, PNG, JPG o AVIF'));
  return uploadFile(file, type, onProgress);
}

/** ¿Es un PDF? Algunos sistemas no informan el tipo: se mira también la extensión. */
export const isPdf = (file: File) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/** Sube un PDF (página de Servicios). La API comprueba que de verdad lo sea. */
export async function uploadPdf(file: File, onProgress?: (p: number) => void): Promise<Media> {
  if (!isPdf(file)) throw new Error(t('Aquí solo van archivos PDF'));
  return uploadFile(file, 'application/pdf', onProgress);
}

function guessType(name: string): string {
  const ext = name.toLowerCase().split('.').pop();
  return ({ webp: 'image/webp', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', avif: 'image/avif' } as Record<string, string>)[ext ?? ''] ?? '';
}

/** Sube varias en orden (las fotos 360 importan en orden) y devuelve las que salieron bien. */
export async function uploadMany(
  files: File[],
  onEach?: (done: number, total: number) => void,
): Promise<{ media: Media[]; failed: { name: string; error: string }[] }> {
  const media: Media[] = [];
  const failed: { name: string; error: string }[] = [];
  const sorted = [...files].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  for (const [i, file] of sorted.entries()) {
    try {
      media.push(await uploadImage(file));
    } catch (e) {
      failed.push({ name: file.name, error: e instanceof Error ? e.message : 'Error' });
    }
    onEach?.(i + 1, sorted.length);
  }
  return { media, failed };
}
